/**
 * AP-ACCEPT-01B: Submit Delivery Evidence
 * Route: /portal/deliveries/submit?poId=... (protected)
 *
 * Supplier submits proof of completed services for a specific issued PO.
 * Per-line: delivered_quantity, delivery_reference, delivery_date, evidence docs, notes.
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, FileText, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { usePortalUser } from "@/components/PortalProtectedRoute";

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: 0 }).format(minor / 100);
}

export default function SupplierSubmitDelivery() {
  const { portalUser } = usePortalUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPoId = searchParams.get("poId") || "";

  const [pos, setPos] = useState([]);
  const [selectedPoId, setSelectedPoId] = useState(preselectedPoId);
  const [poDetail, setPoDetail] = useState(null);
  const [loadingPO, setLoadingPO] = useState(false);

  const [lineSubmissions, setLineSubmissions] = useState([]);
  const [submissionNotes, setSubmissionNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Load available ISSUED POs
  useEffect(() => {
    axios.get(`${API}/api/portal/po`, { params: { status: "ISSUED", page_size: 50 }, withCredentials: true })
      .then((r) => setPos(r.data.items || []));
  }, []);

  // Load PO detail when selected
  useEffect(() => {
    if (!selectedPoId) { setPoDetail(null); setLineSubmissions([]); return; }
    setLoadingPO(true);
    axios.get(`${API}/api/portal/po/${selectedPoId}`, { withCredentials: true })
      .then((r) => {
        setPoDetail(r.data);
        setLineSubmissions((r.data.lines || []).map((line) => ({
          po_line_id: line.id,
          description: line.description,
          po_line_quantity: line.quantity,
          unit: line.unit,
          unit_rate_minor: line.unit_rate_minor,
          currency: r.data.currency_code,
          enabled: true,
          delivered_quantity: "",
          delivery_reference: "",
          delivery_date: "",
          milestone: line.delivery_milestone || "",
          notes: "",
          evidence_documents: [],
        })));
      })
      .catch(() => toast.error("Failed to load PO details"))
      .finally(() => setLoadingPO(false));
  }, [selectedPoId]);

  function updateLine(idx, field, value) {
    setLineSubmissions((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  }

  function addEvidenceDoc(lineIdx) {
    setLineSubmissions((prev) => prev.map((l, i) =>
      i === lineIdx ? { ...l, evidence_documents: [...l.evidence_documents, { document_name: "", document_type: "DELIVERY_NOTE", description: "" }] } : l
    ));
  }

  function updateEvidenceDoc(lineIdx, docIdx, field, value) {
    setLineSubmissions((prev) => prev.map((l, i) => {
      if (i !== lineIdx) return l;
      return { ...l, evidence_documents: l.evidence_documents.map((d, j) => j === docIdx ? { ...d, [field]: value } : d) };
    }));
  }

  function removeEvidenceDoc(lineIdx, docIdx) {
    setLineSubmissions((prev) => prev.map((l, i) =>
      i === lineIdx ? { ...l, evidence_documents: l.evidence_documents.filter((_, j) => j !== docIdx) } : l
    ));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const enabledLines = lineSubmissions.filter((l) => l.enabled);
    if (!enabledLines.length) { setError("At least one line must be included"); return; }

    for (const l of enabledLines) {
      if (!l.delivered_quantity || isNaN(parseFloat(l.delivered_quantity)) || parseFloat(l.delivered_quantity) <= 0) {
        setError(`Delivered quantity for "${l.description.slice(0, 40)}" must be a positive number`); return;
      }
      if (!l.delivery_reference.trim()) {
        setError(`Delivery reference is required for "${l.description.slice(0, 40)}"`); return;
      }
      if (!l.delivery_date) {
        setError(`Delivery date is required for "${l.description.slice(0, 40)}"`); return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        po_id: selectedPoId,
        submission_notes: submissionNotes.trim() || undefined,
        line_submissions: enabledLines.map((l) => ({
          po_line_id: l.po_line_id,
          delivered_quantity: String(parseFloat(l.delivered_quantity)),
          delivery_reference: l.delivery_reference.trim(),
          delivery_date: l.delivery_date,
          milestone: l.milestone || undefined,
          notes: l.notes.trim() || undefined,
          evidence_documents: l.evidence_documents.filter((d) => d.document_name.trim()),
        })),
      };
      const res = await axios.post(`${API}/api/portal/deliveries`, payload, { withCredentials: true });
      toast.success("Delivery evidence submitted successfully");
      navigate(`/portal/deliveries/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="submit-delivery-page" style={{ minHeight: "100vh", background: "#FBF7F2", fontFamily: "'DM Sans', sans-serif" }}>
      <nav style={{ background: "#1A1A1A", borderBottom: "1px solid rgba(201,161,95,0.2)", padding: "0 24px", height: "60px", display: "flex", alignItems: "center", gap: "16px", position: "sticky", top: 0, zIndex: 10 }}>
        <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "18px", fontWeight: 600, color: "#C9A15F" }}>Prashasta</span>
        <span style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.15)" }} />
        <span style={{ fontSize: "12px", color: "#8A7A6A" }}>Partner Portal</span>
      </nav>

      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "32px 24px" }}>
        <button onClick={() => navigate("/portal/deliveries")} style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#8A7A6A", background: "none", border: "none", cursor: "pointer", padding: "0 0 20px" }}>
          <ArrowLeft size={14} /> Back to My Deliveries
        </button>

        <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "clamp(20px, 3vw, 26px)", fontWeight: 600, color: "#1A1A1A", margin: "0 0 4px" }}>Submit Delivery Evidence</h2>
        <p style={{ fontSize: "13px", color: "#6B5B4A", marginBottom: "28px" }}>Submit proof of completed services against an issued Purchase Order</p>

        <form onSubmit={handleSubmit}>
          {/* PO Selection */}
          <div style={{ background: "#FFFFFF", border: "1px solid #EDE5D8", borderRadius: "12px", padding: "20px 24px", marginBottom: "16px" }}>
            <label style={labelStyle}>Select Purchase Order *</label>
            <select
              data-testid="po-select"
              value={selectedPoId}
              onChange={(e) => setSelectedPoId(e.target.value)}
              required
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              <option value="">— Choose an issued PO —</option>
              {pos.map((po) => (
                <option key={po.id} value={po.id}>
                  {po.po_number || po.id.slice(0, 8)} — {po.event_name_snapshot || "Event"} ({fmt(po.total_minor, po.currency_code)})
                </option>
              ))}
            </select>
            {loadingPO && <p style={{ fontSize: "12px", color: "#9A8A78", marginTop: "6px" }}>Loading PO…</p>}
          </div>

          {/* Submission notes */}
          {poDetail && (
            <div style={{ background: "#FFFFFF", border: "1px solid #EDE5D8", borderRadius: "12px", padding: "20px 24px", marginBottom: "16px" }}>
              <label style={labelStyle}>Submission notes (optional)</label>
              <textarea
                data-testid="submission-notes"
                value={submissionNotes}
                onChange={(e) => setSubmissionNotes(e.target.value)}
                placeholder="General notes about this delivery (batch number, dispatch date, etc.)"
                rows={2}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>
          )}

          {/* Line submissions */}
          {poDetail && lineSubmissions.map((line, idx) => (
            <div
              key={line.po_line_id}
              data-testid={`line-submission-${idx}`}
              style={{ background: "#FFFFFF", border: `1px solid ${line.enabled ? "#EDE5D8" : "#F0EBE0"}`, borderRadius: "12px", padding: "20px 24px", marginBottom: "12px", opacity: line.enabled ? 1 : 0.5 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                <div>
                  <p style={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A", margin: 0 }}>{line.description}</p>
                  <p style={{ fontSize: "11px", color: "#9A8A78", margin: "2px 0 0" }}>
                    PO Qty: {line.po_line_quantity} {line.unit} · {fmt(line.unit_rate_minor, line.currency)}/unit
                  </p>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#6B5B4A", cursor: "pointer" }}>
                  <input type="checkbox" checked={line.enabled} onChange={(e) => updateLine(idx, "enabled", e.target.checked)} />
                  Include this line
                </label>
              </div>

              {line.enabled && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                  <div>
                    <label style={smallLabelStyle}>Delivered Quantity *</label>
                    <input data-testid={`qty-${idx}`} type="number" min="0.001" step="0.001" value={line.delivered_quantity} onChange={(e) => updateLine(idx, "delivered_quantity", e.target.value)} required={line.enabled} placeholder={`max ${line.po_line_quantity}`} style={inputStyle} />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>Delivery Reference *</label>
                    <input data-testid={`ref-${idx}`} type="text" value={line.delivery_reference} onChange={(e) => updateLine(idx, "delivery_reference", e.target.value)} required={line.enabled} placeholder="Challan / delivery note #" style={inputStyle} />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>Delivery Date *</label>
                    <input data-testid={`date-${idx}`} type="date" value={line.delivery_date} onChange={(e) => updateLine(idx, "delivery_date", e.target.value)} required={line.enabled} style={inputStyle} />
                  </div>
                  {line.milestone !== undefined && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={smallLabelStyle}>Milestone (if applicable)</label>
                      <input type="text" value={line.milestone} onChange={(e) => updateLine(idx, "milestone", e.target.value)} placeholder="e.g. Reception Day" style={inputStyle} />
                    </div>
                  )}
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={smallLabelStyle}>Notes (optional)</label>
                    <input type="text" value={line.notes} onChange={(e) => updateLine(idx, "notes", e.target.value)} placeholder="Any additional delivery notes" style={inputStyle} />
                  </div>
                </div>
              )}

              {/* Evidence documents */}
              {line.enabled && (
                <div style={{ borderTop: "1px solid #F0EBE0", paddingTop: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#9A8A78", letterSpacing: "0.5px", textTransform: "uppercase" }}>Evidence Documents</span>
                    <button type="button" onClick={() => addEvidenceDoc(idx)} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#C9A15F", background: "none", border: "none", cursor: "pointer" }}>
                      <Plus size={11} /> Add document
                    </button>
                  </div>
                  {line.evidence_documents.map((doc, dIdx) => (
                    <div key={dIdx} style={{ display: "grid", gridTemplateColumns: "1fr 120px 1fr auto", gap: "8px", marginBottom: "8px", alignItems: "start" }}>
                      <input placeholder="Document name / filename" value={doc.document_name} onChange={(e) => updateEvidenceDoc(idx, dIdx, "document_name", e.target.value)} style={{ ...inputStyle, fontSize: "11px" }} />
                      <select value={doc.document_type} onChange={(e) => updateEvidenceDoc(idx, dIdx, "document_type", e.target.value)} style={{ ...inputStyle, fontSize: "11px", cursor: "pointer" }}>
                        {["CHALLAN", "DELIVERY_NOTE", "INSPECTION_REPORT", "PHOTO", "INVOICE_DRAFT", "OTHER"].map((t) => (
                          <option key={t} value={t}>{t.replace("_", " ")}</option>
                        ))}
                      </select>
                      <input placeholder="Description (optional)" value={doc.description || ""} onChange={(e) => updateEvidenceDoc(idx, dIdx, "description", e.target.value)} style={{ ...inputStyle, fontSize: "11px" }} />
                      <button type="button" onClick={() => removeEvidenceDoc(idx, dIdx)} style={{ padding: "8px", background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}><Trash2 size={13} /></button>
                    </div>
                  ))}
                  {line.evidence_documents.length === 0 && <p style={{ fontSize: "11px", color: "#C4B39A" }}>No evidence documents added. Adding delivery notes and challan references is recommended.</p>}
                </div>
              )}
            </div>
          ))}

          {error && (
            <div data-testid="submit-error" style={{ display: "flex", gap: "8px", padding: "12px", background: "rgba(220,50,50,0.06)", border: "1px solid rgba(220,50,50,0.2)", borderRadius: "8px", marginBottom: "16px" }}>
              <AlertCircle size={14} style={{ color: "#DC2626", flexShrink: 0, marginTop: "1px" }} />
              <span style={{ fontSize: "13px", color: "#DC2626" }}>{error}</span>
            </div>
          )}

          {poDetail && (
            <button type="submit" data-testid="submit-delivery-form-btn" disabled={submitting} style={{ width: "100%", padding: "13px", background: submitting ? "#C9A15F80" : "#C9A15F", color: "#111", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer" }}>
              {submitting ? "Submitting…" : "Submit Delivery Evidence"}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

const labelStyle = { display: "block", fontSize: "12px", color: "#6B5B4A", marginBottom: "6px", fontWeight: 500 };
const smallLabelStyle = { display: "block", fontSize: "10px", color: "#9A8A78", marginBottom: "4px", letterSpacing: "0.5px", textTransform: "uppercase" };
const inputStyle = { width: "100%", padding: "9px 12px", border: "1px solid #D4C5B0", borderRadius: "8px", fontSize: "13px", color: "#1A1A1A", background: "#FAFAF8", outline: "none", boxSizing: "border-box", fontFamily: "'DM Sans', sans-serif" };
